import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Convert https://... to wss://... (or http:// to ws://)
function wsUrl(token) {
  const u = new URL(BACKEND_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}${u.pathname.replace(/\/$/, "")}/api/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Live WebSocket connection to /api/ws.
 * Handles reconnection with backoff. Exposes `send` for subscription messages.
 *
 * onMessage receives the parsed payload: { type: "dashboard"|"campaign"|"pong", ... }
 */
export function useLiveSocket(onMessage) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const onMessageRef = useRef(onMessage);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    let closedByUs = false;
    let reconnectTimer = null;

    const connect = () => {
      const token = localStorage.getItem("ce_token");
      if (!token) return;
      const ws = new WebSocket(wsUrl(token));
      socketRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setReconnectAttempt(0);
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          onMessageRef.current?.(data);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        socketRef.current = null;
        if (!closedByUs) {
          setReconnectAttempt((n) => n + 1);
          const delay = Math.min(15000, 1000 * Math.pow(1.5, Math.min(reconnectAttempt, 8)));
          reconnectTimer = setTimeout(connect, delay);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();

    // Keepalive ping every 25s
    const pingIv = setInterval(() => {
      const s = socketRef.current;
      if (s && s.readyState === WebSocket.OPEN) {
        try { s.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
      }
    }, 25000);

    return () => {
      closedByUs = true;
      clearInterval(pingIv);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { socketRef.current?.close(); } catch { /* noop */ }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback((msg) => {
    const s = socketRef.current;
    if (s && s.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  return { connected, send };
}
